import { Configs } from "../configs";
import { keysToCamel, keysToSnakeV2 } from "../utils";
import { ClassConstructor, plainToInstance } from "class-transformer";
import {
    SurveyData,
    ResumedSurveyData,
} from "./models/surveyModels";

export class Request {

    static redirectToLogin() {
        import("@/router").then(({ default: router }) => {
            router.push({ name: "login" })
        })
    }

    static async get<T>(slug: string, errorMessage?: string, classConstructor?: ClassConstructor<T>): Promise<T | undefined> {
        const url = `${Configs.env.BASE_URL}/${slug}`
        
        try {
            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("accessToken")}`
                }
            })

            if (!response.ok) {
                if (response.status === 401) {
                    this.redirectToLogin()
                }
                return undefined
            }

            const data = await response.json()
            const onCamel = keysToCamel(data) as T

            if (!classConstructor) {
                return onCamel
            }

            return plainToInstance<T, any>(classConstructor, onCamel, {
                excludeExtraneousValues: false,
                enableImplicitConversion: true,
            }) as T

        } catch (error) {
            const message = errorMessage || "Error getting data: "
            console.warn(message, error)
        }
    }

    static async download(slug: string, errorMessage?: string): Promise<boolean | undefined> {
        const url = `${Configs.env.BASE_URL}/${slug}`

        try {
            const response = await fetch(url, {
                headers: {
                    "Authorization": `Bearer ${localStorage.getItem("accessToken")}`
                }
            })

            if (!response.ok) {
                if (response.status === 401) {
                    this.redirectToLogin()
                }
                return undefined
            }

            const data = await response.blob()
            const fileUrl = window.URL.createObjectURL(data)

            const date = new Date()
            const localDate = date.toLocaleDateString().replaceAll('/', '-')
            const fileName = `EUT-${localDate}.xlsx`

            const link = document.createElement('a');
            link.href = fileUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.remove();

            return true
        }
        catch (error) {
            const message = errorMessage || "Error getting data: "
            console.warn(message, error)
            return false
        }
    }


    static async post<T>(slug: string, body: any, errorMessage?: string, formData = false, classConstructor?: ClassConstructor<T>): Promise<T | undefined> {
        const url = `${Configs.env.BASE_URL}/${slug}`

        try {
            const response = await fetch(url, {
                method: "POST",
                headers: !formData ? {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${localStorage.getItem("accessToken")}`
                } : {
                    "Authorization": `Bearer ${localStorage.getItem("accessToken")}`
                },
                body: formData ? body : JSON.stringify(keysToSnakeV2(body))
            })
            if (!response.ok) {
                if (response.status === 401) {
                    this.redirectToLogin()
                }
                return undefined
            }

            const data = await response.json()
            const onCamel = keysToCamel(data) as T
            if (!classConstructor) {
                return onCamel
            }

            return plainToInstance<T, any>(classConstructor, onCamel, {
                excludeExtraneousValues: false,
                enableImplicitConversion: true,
            }) as T
        } catch (error) {
            const message = errorMessage || "Error posting data: "
            console.warn(message, error)
        }
    }

    static async delete(slug: string, errorMessage?: string): Promise<boolean> {
        const url = `${Configs.env.BASE_URL}/${slug}`

        try {
            const response = await fetch(url, {
                method: "DELETE",
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem("accessToken")}`
                }
            })

            if (!response.ok) {
                return false
            }

            return true
        } catch (error) {
            const message = errorMessage || "Error deleting data: "
            console.warn(message, error)
            return false
        }
    }
}

export class SurveyRespondeService {

    async saveSurvey(data: SurveyData): Promise<SurveyData | undefined> {
        return Request.post<SurveyData>("survey_data/", data, "Error saving survey");
    }

    async getSurvey(id: number): Promise<SurveyData | undefined> {
        return Request.get<SurveyData>(`survey_data/${id}`, "Error getting survey");
    }

    async getAllSurveys(): Promise<ResumedSurveyData[] | undefined> {
        return Request.get<ResumedSurveyData[]>("survey_data/", "Error getting surveys");
    }

    async deleteSurvey(id: number): Promise<boolean> {
        return Request.delete(`survey_data/${id}`, "Error deleting survey");
    }
}
